import {
  SmartMatchingData,
  SmartMatchingDataPerson,
} from "~/SmartMatching/SmartMatchingContext"

// Use the actual GraphQL types from SmartMatchingContext
export type EmbeddingField = SmartMatchingData["embeddingFields"][0]
export type EmbeddingFieldType = EmbeddingField["field"]
export type EmbeddingModelType = EmbeddingField["model"]

export type FieldScore = {
  embeddingFieldId: number
  score: number
  distance: number
}

export type ResolvedFieldScore = FieldScore & {
  embeddingField: EmbeddingField
}

/**
 * Resolves a field score by mapping its embeddingFieldId to the corresponding embedding field metadata
 */
export function resolveFieldScore(
  fieldScore: FieldScore,
  embeddingFields: SmartMatchingData["embeddingFields"],
): ResolvedFieldScore | null {
  const embeddingField = embeddingFields.find(
    (field) => field.id === fieldScore.embeddingFieldId,
  )

  if (!embeddingField) {
    return null
  }

  return {
    ...fieldScore,
    embeddingField,
  }
}

/**
 * Gets field scores for a specific field type and model, handling special cases for skills and custom select options
 */
export function getFieldScoreByType(
  smartMatchingData: SmartMatchingDataPerson,
  embeddingFields: SmartMatchingData["embeddingFields"],
  fieldType: EmbeddingFieldType,
  model: EmbeddingModelType = "PERSON",
): number | undefined {
  // Special case for skills: use matchedSkillsScore
  if (fieldType === "NAME" && model === "SKILL") {
    return smartMatchingData.scores.matchedSkillsScore
  }

  // Special case for custom select options: use matchedCustomSelectOptionsScore
  // TODO Get custom field specific score
  if (fieldType === "CUSTOM_SELECT") {
    return smartMatchingData.scores.matchedCustomSelectOptionsScore
  }

  // For all other fields, use the fieldScores data
  const fieldScores = smartMatchingData.scores.fieldScores

  for (const fieldScore of fieldScores) {
    const resolved = resolveFieldScore(fieldScore, embeddingFields)
    if (
      resolved &&
      resolved.embeddingField.field === fieldType &&
      resolved.embeddingField.model === model &&
      resolved.embeddingField.isIncluded
    ) {
      return resolved.score
    }
  }

  return undefined
}

/**
 * Checks if a specific field type is included/enabled for matching
 */
export function isFieldTypeIncluded(
  embeddingFields: SmartMatchingData["embeddingFields"],
  fieldType: EmbeddingFieldType,
  model: EmbeddingModelType = "PERSON",
  customSelectTypeId?: number,
): boolean {
  return embeddingFields.some(
    (field) =>
      field.field === fieldType &&
      field.model === model &&
      field.isIncluded &&
      (fieldType !== "CUSTOM_SELECT" ||
        field.customSelectTypeId === customSelectTypeId),
  )
}
